import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CalendarClock, CalendarDays, ChevronLeft, ChevronRight, Copy, Pencil, Plus, Trash2, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type DepartmentRef, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { MONTH_NAMES, formatDate, formatDays, formatMinutes, isoDate } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { SupportModeTab } from './SupportMode';

/** §37.5 — textual labels stay clear whether or not colour renders. */
const CELL_STYLE: Record<string, string> = {
  MORNING: 'bg-signal/18 text-signal-800 ring-signal/35 dark:text-signal',
  NIGHT: 'bg-ai-plasma/18 text-ai-plasma-text dark:text-ai-plasma-lift ring-ai-plasma/35',
  CUSTOM: 'bg-ai-circuit/15 text-info-fg dark:text-info-dark ring-ai-circuit/35',
  OFF: 'bg-neutral-500/10 text-[var(--text-secondary)] ring-neutral-500/25',
};

/**
 * The API returns the English M/N/C/O labels. The grid derives its own so the cells
 * match the Arabic legend instead of contradicting it.
 */
const CELL_LABEL: Record<'en' | 'ar', Record<string, string>> = {
  en: { MORNING: 'M', NIGHT: 'N', CUSTOM: 'C', OFF: 'O' },
  ar: { MORNING: 'ص', NIGHT: 'ل', CUSTOM: 'خ', OFF: 'إ' },
};

/**
 * Support mode's days (2026-11-01): a day with support hours and no shift shows S; a day with both, its shift's
 * letter and a green dot. Either way the hours are in its title, and its page sets them.
 */
const SUPPORT_LABEL: Record<'en' | 'ar', string> = { en: 'S', ar: 'د' };
const SUPPORT_STYLE = 'bg-success/15 text-success-fg ring-success/35 dark:text-success';

const SHIFT_TABS = ['roster', 'definitions', 'holidays', 'support'];

export function ShiftsPage() {
  const { t } = useI18n();
  // The tab is in the address, so a definition's form page can send you back to it.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') ?? 'roster';
  const tab = SHIFT_TABS.includes(requested) ? requested : 'roster';

  return (
    <>
      <PageHeader title={t('shifts.title')} subtitle={t('shifts.noEvening')} />
      <Tabs
        active={tab}
        onChange={(id) => setParams(id === 'roster' ? {} : { tab: id }, { replace: true })}
        tabs={[
          { id: 'roster', label: t('shifts.roster') },
          { id: 'definitions', label: t('shifts.definitions') },
          { id: 'holidays', label: t('shifts.holidays') },
          // Support mode (2026-11-01): not a shift — who may switch it on in the phone app, and when.
          { id: 'support', label: t('shifts.support') },
        ]}
      />
      <div className="mt-5">
        {tab === 'roster' && <RosterGrid />}
        {tab === 'definitions' && <ShiftDefinitions />}
        {tab === 'holidays' && <Holidays />}
        {tab === 'support' && <SupportModeTab />}
      </div>
    </>
  );
}

function RosterGrid() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [departmentId, setDepartmentId] = useState('');

  const { data: departments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<{ items: DepartmentRef[] }>('/departments', { pageSize: 100 }),
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['roster', year, month, departmentId],
    queryFn: () =>
      api.get<any>('/shift-schedules/monthly-roster', { year, month, departmentId: departmentId || undefined }),
  });

  const shiftMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const holidayDates = new Set((data?.holidays ?? []).map((h: any) => h.date));

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="flex flex-wrap items-center gap-3 p-4">
          <div className="flex items-center gap-1">
            <Button size="sm" icon={<ChevronLeft className="h-4 w-4 flip-rtl" />} onClick={() => shiftMonth(-1)} />
            <span className="min-w-[160px] text-center text-title text-[var(--text-primary)]">
              {MONTH_NAMES[locale][month - 1]} {year}
            </span>
            <Button size="sm" icon={<ChevronRight className="h-4 w-4 flip-rtl" />} onClick={() => shiftMonth(1)} />
          </div>

          <Select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            className="!w-auto min-w-[180px]"
          >
            <option value="">
              {can(P.USERS_VIEW_ALL) ? `${t('common.department')} — ${t('common.all')}` : t('reach.allMyDepartments')}
            </option>
            {departments?.items.map((d) => (
              <option key={d.id} value={d.id}>{localized(d)}</option>
            ))}
          </Select>

          <span className="ms-auto text-caption text-muted">{t('shifts.legend')}</span>
          <span className="inline-flex items-center gap-1.5 text-caption text-muted">
            <span className="h-2 w-2 rounded-full bg-success" aria-hidden />
            {t('shifts.supportLegend', { letter: SUPPORT_LABEL[locale] })}
          </span>

          {can(P.SHIFTS_MANAGE) && (
            <>
              <Button size="sm" icon={<Copy className="h-4 w-4" />} onClick={() => navigate('/shifts/copy')}>
                {t('shifts.copy')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                icon={<Wand2 className="h-4 w-4" />}
                onClick={() => navigate(`/shifts/generate?year=${year}&month=${month}`)}
              >
                {t('shifts.generate')}
              </Button>
            </>
          )}
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.rows.length ? (
          <EmptyState icon={<CalendarDays className="h-8 w-8" />} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky start-0 z-10 border-b hairline bg-[var(--surface-card)] px-3 py-2 text-start text-overline uppercase text-[var(--text-tertiary)]">
                    {t('nav.employees')}
                  </th>
                  {data.days.map((day: string) => {
                    const dayNum = Number(day.slice(-2));
                    const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
                    const isWeekend = weekday === 5 || weekday === 6;
                    return (
                      <th
                        key={day}
                        className={clsx(
                          'border-b hairline px-0.5 py-2 text-center text-caption tabular',
                          holidayDates.has(day)
                            ? 'bg-primary/10 text-primary-600 dark:text-primary-400'
                            : isWeekend
                              ? 'text-[var(--text-tertiary)]'
                              : 'text-[var(--text-secondary)]',
                        )}
                        title={holidayDates.has(day) ? data.holidays.find((h: any) => h.date === day)?.name : undefined}
                      >
                        {dayNum}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row: any) => (
                  <tr key={row.user.id}>
                    <td className="sticky start-0 z-10 border-b hairline bg-[var(--surface-card)] px-3 py-1.5 text-body-sm">
                      <span className="block truncate">{localized(row.user)}</span>
                      {row.user.jobTitle && (
                        <span className="block truncate text-caption text-faint">{row.user.jobTitle}</span>
                      )}
                    </td>
                    {row.cells.map((cell: any) => {
                      const support = cell.support as { startTime: string; endTime: string } | null;
                      const titles = [
                        cell.type &&
                          `${enumLabel(cell.type, locale)}${cell.crossesMidnight ? ' · ' + t('shifts.crossesMidnight') : ''}`,
                        support && t('shifts.supportCell', { hours: `${support.startTime}–${support.endTime}` }),
                      ].filter(Boolean);
                      return (
                        <td key={cell.date} className="border-b hairline p-0.5 text-center">
                          <button
                            type="button"
                            disabled={!can(P.SHIFTS_MANAGE)}
                            onClick={() =>
                              navigate(
                                `/shifts/day?userId=${row.user.id}` +
                                  `&userName=${encodeURIComponent(row.user.fullName)}&date=${cell.date}`,
                              )
                            }
                            title={titles.length ? titles.join(' · ') : undefined}
                            className={clsx(
                              'relative grid h-7 w-7 place-items-center rounded-sm text-caption font-bold ring-1 ring-inset transition-colors',
                              cell.type
                                ? CELL_STYLE[cell.type]
                                : support
                                  ? SUPPORT_STYLE
                                  : 'text-[var(--text-tertiary)] ring-transparent hover:ring-[var(--line)]',
                              can(P.SHIFTS_MANAGE) && 'hover:ring-primary cursor-pointer',
                            )}
                          >
                            {cell.type ? CELL_LABEL[locale][cell.type] : support ? SUPPORT_LABEL[locale] : '·'}
                            {cell.type && support && (
                              <span
                                className="absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full bg-success ring-2 ring-[var(--surface-card)]"
                                aria-hidden
                              />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

    </>
  );
}

function ShiftDefinitions() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['shift-definitions', 'all'],
    queryFn: () => api.get<any[]>('/shift-definitions', { includeInactive: 'true' }),
  });

  if (isLoading) return <LoadingBlock />;

  return (
    <>
      <Card padded={false}>
        <CardHeader
          title={t('shifts.definitions')}
          subtitle={t('shifts.noEvening')}
          className="p-5 pb-0"
          action={
            can(P.SHIFTS_MANAGE) && (
              <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/shifts/definitions/new')}>
                {t('common.create')}
              </Button>
            )
          }
        />
        <Table>
          <thead>
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('common.type')}</Th>
              <Th>{t('shifts.startTime')}</Th>
              <Th>{t('shifts.endTime')}</Th>
              <Th>{t('shifts.crossesMidnight')}</Th>
              <Th align="end">{t('shifts.standardHours')}</Th>
              <Th>{t('shifts.earlyEndCol')}</Th>
              {can(P.SHIFTS_MANAGE) && <Th><span className="sr-only">{t('common.edit')}</span></Th>}
            </tr>
          </thead>
          <tbody>
            {data?.map((d) => (
              <Tr key={d.id}>
                <Td>
                  <span className="flex flex-wrap items-center gap-2">
                    {(locale === 'ar' && d.nameAr) || d.name}
                    {!d.isActive && <Badge tone="neutral">{t('settings.switchedOff')}</Badge>}
                  </span>
                  {d.nextFrom && (
                    <span className="mt-0.5 flex items-center gap-1 text-caption text-warning-fg dark:text-warning-dark">
                      <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {t('shifts.nextHours', {
                        hours: `${d.nextStartTime}–${d.nextEndTime}`,
                        date: formatDate(d.nextFrom, locale),
                      })}
                    </span>
                  )}
                </Td>
                <Td><Badge tone="neutral">{enumLabel(d.type, locale)}</Badge></Td>
                <Td className="font-mono tabular">{d.startTime}</Td>
                <Td className="font-mono tabular">{d.endTime}</Td>
                <Td>
                  {d.crossesMidnight ? (
                    <Badge tone="plasma">{t('common.yes')}</Badge>
                  ) : (
                    <span className="text-faint">{t('common.no')}</span>
                  )}
                </Td>
                <Td align="end" className="tabular">{Number(d.standardHours)}</Td>
                {/* Ending the shift early (2026-11-03): allowed so long before the end, and what earlier costs. */}
                <Td className="tabular whitespace-nowrap">
                  {d.earlyEndMinutes != null ? (
                    t('shifts.earlyEndRule', {
                      minutes: formatMinutes(d.earlyEndMinutes, locale),
                      days: formatDays(d.earlyEndPenaltyDays, locale),
                    })
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </Td>
                {can(P.SHIFTS_MANAGE) && (
                  <Td align="end">
                    <span className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Pencil className="h-4 w-4" />}
                        onClick={() => navigate(`/shifts/definitions/${d.id}/edit`)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 className="h-4 w-4" />}
                        onClick={() => navigate(`/shifts/definitions/${d.id}/remove`)}
                      >
                        {t('common.remove')}
                      </Button>
                    </span>
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </Table>
      </Card>

    </>
  );
}

function Holidays() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => api.get<any[]>('/holidays'),
  });

  if (isLoading) return <LoadingBlock />;

  return (
    <>
      <Card padded={false}>
        <CardHeader
          title={t('shifts.holidays')}
          className="p-5 pb-0"
          action={
            can(P.SHIFTS_MANAGE) && (
              <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/shifts/holidays/new')}>
                {t('common.create')}
              </Button>
            )
          }
        />
        <Table>
          <thead>
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('common.date')}</Th>
              {can(P.SHIFTS_MANAGE) && <Th><span className="sr-only">{t('common.edit')}</span></Th>}
            </tr>
          </thead>
          <tbody>
            {data?.map((h) => (
              <Tr key={h.id}>
                <Td>{(locale === 'ar' && h.nameAr) || h.name}</Td>
                <Td className="tabular">{formatDate(h.date, locale)}</Td>
                {can(P.SHIFTS_MANAGE) && (
                  <Td align="end">
                    <span className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Pencil className="h-4 w-4" />}
                        onClick={() => navigate(`/shifts/holidays/${h.id}/edit`)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 className="h-4 w-4" />}
                        onClick={() => navigate(`/shifts/holidays/${h.id}/delete`)}
                      >
                        {t('common.delete')}
                      </Button>
                    </span>
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </Table>
      </Card>

    </>
  );
}
